import { Box, Chip, Skeleton } from '@mui/material'
import { Button } from '@ds/components'
import type { TAnalytics } from '@muse/analytics'
import { type MRT_ColumnDef as ColumnDef } from 'material-react-table'
import {
  formatApiDate,
  getEnumNameById,
  isCopyrightRequest,
  isApprovalRequest,
  PublisherApproveRequestStatus,
  PublisherRequestTypeEnum,
} from '@modules/api-core'
import type { TPublisherRequest, TEnumItem } from '@modules/types'
import { TRequestEventName } from '@publisher/modules/analytics/channels'
import { getStatusTone } from '../../utils/requestStatus'
import {
  RequestTypeColumnHeader,
  type TPublisherRequestListTypeFilter,
} from './RequestTypeColumnHeader'
import {
  ReviewTypeColumnHeader,
  type TPublisherRequestListReviewTypeFilter,
} from './ReviewTypeColumnHeader'
import { StatusColumnHeader, type TPublisherRequestListStatusFilter } from './StatusColumnHeader'
import { REQUESTS_LIST_COLUMNS_STRINGS } from './strings'

const dateCell = ({ cell }: Parameters<NonNullable<ColumnDef<TPublisherRequest>['Cell']>>[0]) => {
  const value = cell.getValue<string>()
  return value ? formatApiDate(value) : ''
}

interface CreateRequestsListColumnsProps {
  enums: TEnumItem[]
  enumsLoading: boolean
  isFetching: boolean
  go: (options: {
    to: string
    query?: { to: string }
    options?: { keepQuery: boolean }
    type: 'replace' | 'push'
  }) => void
  pathname: string
  showUrl: (resource: string, id: number) => string
  analytics: TAnalytics
  channels: {
    request?: {
      send: (data: { event: TRequestEventName; requestId: number }) => void
    }
  }
  requestTypeFilter: TPublisherRequestListTypeFilter
  onRequestTypeFilterChange: (filter: TPublisherRequestListTypeFilter) => void
  reviewTypeFilter: TPublisherRequestListReviewTypeFilter
  onReviewTypeFilterChange: (filter: TPublisherRequestListReviewTypeFilter) => void
  statusFilter: TPublisherRequestListStatusFilter
  onStatusFilterChange: (filter: TPublisherRequestListStatusFilter) => void
}

export const createRequestsListColumns = ({
  enums,
  enumsLoading,
  isFetching,
  go,
  pathname,
  showUrl,
  analytics,
  channels,
  requestTypeFilter,
  onRequestTypeFilterChange,
  reviewTypeFilter,
  onReviewTypeFilterChange,
  statusFilter,
  onStatusFilterChange,
}: CreateRequestsListColumnsProps): ColumnDef<TPublisherRequest>[] => {
  const requestTypeCell = ({
    cell,
  }: Parameters<NonNullable<ColumnDef<TPublisherRequest>['Cell']>>[0]) => {
    const typeId = cell.getValue<number>()
    if (enumsLoading || isFetching) return <Skeleton width={80} height={20} />
    return getEnumNameById(enums, 'cart_item_type', typeId)
  }

  const statusCell = ({
    cell,
  }: Parameters<NonNullable<ColumnDef<TPublisherRequest>['Cell']>>[0]) => {
    const statusData = cell.getValue<{
      statusId: number
      type: 'approve' | 'copyright'
    }>()

    if (enumsLoading || isFetching) {
      return <Skeleton width={52} height={32} variant="rectangular" sx={{ borderRadius: 16 }} />
    }

    const enumName =
      statusData.type === 'approve'
        ? 'publisher_approve_request_status_enum'
        : 'publisher_copyright_request_status_enum'

    const status = getEnumNameById(enums, enumName, statusData.statusId)
    return (
      <Chip
        label={status}
        size="medium"
        variant="filled"
        sx={{
          ...getStatusTone(status),
          height: 'auto',
          py: 0.75,
          fontSize: 14,
          fontWeight: 600,
          '.MuiChip-label': { px: 1.75 },
        }}
      />
    )
  }

  const reviewTypeCell = ({
    cell,
  }: Parameters<NonNullable<ColumnDef<TPublisherRequest>['Cell']>>[0]) => {
    const requestType = cell.getValue<number>()
    if (enumsLoading || isFetching) return <Skeleton width={80} height={20} />

    if (isCopyrightRequest(requestType)) {
      return REQUESTS_LIST_COLUMNS_STRINGS.copyright
    }
    if (
      isApprovalRequest(requestType) ||
      requestType === PublisherRequestTypeEnum.LYRICS_APPROVAL_REQUEST ||
      requestType === PublisherRequestTypeEnum.COMMON_APPROVAL_REQUEST
    ) {
      return REQUESTS_LIST_COLUMNS_STRINGS.approval
    }

    return REQUESTS_LIST_COLUMNS_STRINGS.unknown
  }

  const actionsCell = ({
    row,
  }: Parameters<NonNullable<ColumnDef<TPublisherRequest>['Cell']>>[0]) => (
    <Box>
      <Button
        variant="contained"
        disabled={enumsLoading || isFetching}
        size="small"
        color="primary"
        onClick={() => {
          if (analytics && channels.request) {
            channels.request.send({
              event: TRequestEventName.Open,
              requestId: row.original.id,
            })
          }

          return go({
            to: `${showUrl('request', row.original.id)}`,
            query: {
              to: pathname,
            },
            options: {
              keepQuery: true,
            },
            type: 'replace',
          })
        }}
        sx={{
          fontSize: 15,
          fontWeight: 600,
          px: 3,
          py: 1.5,
          whiteSpace: 'nowrap',
        }}
      >
        {REQUESTS_LIST_COLUMNS_STRINGS.reviewRequest}
      </Button>
    </Box>
  )

  return [
    {
      accessorKey: 'clientRequest.cartItemTypeId',
      header: REQUESTS_LIST_COLUMNS_STRINGS.requestType,
      Header: () => (
        <RequestTypeColumnHeader
          enums={enums}
          enumsLoading={enumsLoading}
          requestTypeFilter={requestTypeFilter}
          onRequestTypeFilterChange={onRequestTypeFilterChange}
        />
      ),
      enableSorting: false,
      enableColumnActions: false,
      Cell: requestTypeCell,
    },
    {
      id: 'publisherRequestStatus',
      accessorFn: row => {
        if (row.approveStatus !== null && row.approveStatus !== undefined) {
          return { statusId: row.approveStatus, type: 'approve' }
        }
        if (row.copyrightStatus !== null && row.copyrightStatus !== undefined) {
          return { statusId: row.copyrightStatus, type: 'copyright' }
        }
        return {
          statusId: PublisherApproveRequestStatus.NEW,
          type: isCopyrightRequest(row.type) ? 'copyright' : 'approve',
        }
      },
      header: REQUESTS_LIST_COLUMNS_STRINGS.status,
      Header: () => (
        <StatusColumnHeader
          enums={enums}
          enumsLoading={enumsLoading}
          statusFilter={statusFilter}
          onStatusFilterChange={onStatusFilterChange}
        />
      ),
      enableColumnActions: false,
      enableSorting: false,
      Cell: statusCell,
    },
    {
      accessorKey: 'clientRequest.licenseeName',
      header: REQUESTS_LIST_COLUMNS_STRINGS.licensee,
      enableSorting: false,
      enableColumnActions: false,
    },
    {
      accessorKey: 'clientRequest.publicationAuthorArrangerEditor',
      header: REQUESTS_LIST_COLUMNS_STRINGS.publicationAuthorArrangerEditor,
      enableSorting: false,
      enableColumnActions: false,
    },
    {
      accessorKey: 'clientRequest.publicationType',
      header: REQUESTS_LIST_COLUMNS_STRINGS.publicationType,
      enableSorting: false,
      enableColumnActions: false,
    },
    {
      accessorKey: 'type',
      header: REQUESTS_LIST_COLUMNS_STRINGS.reviewType,
      Header: () => (
        <ReviewTypeColumnHeader
          reviewTypeFilter={reviewTypeFilter}
          onReviewTypeFilterChange={onReviewTypeFilterChange}
        />
      ),
      enableSorting: false,
      enableColumnActions: false,
      Cell: reviewTypeCell,
    },
    {
      accessorKey: 'clientRequest.createdAt',
      header: REQUESTS_LIST_COLUMNS_STRINGS.dateCreated,
      enableColumnActions: false,
      Cell: dateCell,
    },
    {
      accessorKey: 'actions',
      header: REQUESTS_LIST_COLUMNS_STRINGS.actions,
      enableSorting: false,
      enableColumnFilter: false,
      enableColumnActions: false,
      size: 160,
      muiTableHeadCellProps: {
        sx: {
          position: 'sticky',
          right: 0,
          zIndex: 3,
          verticalAlign: 'middle',
          color: 'text.secondary',
          backgroundColor: 'background.paper',
        },
      },
      muiTableBodyCellProps: {
        sx: {
          position: 'sticky',
          right: 0,
          zIndex: 2,
          backgroundColor: 'background.paper',
        },
      },
      Cell: actionsCell,
    },
  ]
}
